import React, { useState, useMemo } from 'react';
import { Label, HelpButton, NumberInput } from '@/components/ui';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { Coffee, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BreakBillingPolicy } from '@/types/MealBreakConfig';
import mealBreakTypeMd from '../meal_break/descriptions/meal_break_type.md?raw';

type BreakMode = 'all_paid' | 'all_unpaid' | 'threshold';

function policyToMode(policy: BreakBillingPolicy | null | undefined): BreakMode {
  if (!policy) return 'all_unpaid';
  if (policy.thresholdMinutes != null) return 'threshold';
  if (policy.upToThreshold === 'paid' && policy.overThreshold === 'paid') return 'all_paid';
  return 'all_unpaid';
}

function modeToPolicy(mode: BreakMode, thresholdMinutes: number | null): BreakBillingPolicy {
  switch (mode) {
    case 'all_paid':
      return { thresholdMinutes: null, upToThreshold: 'paid', overThreshold: 'paid' };
    case 'all_unpaid':
      return { thresholdMinutes: null, upToThreshold: 'unpaid', overThreshold: 'unpaid' };
    case 'threshold':
      return { thresholdMinutes: thresholdMinutes ?? 30, upToThreshold: 'paid', overThreshold: 'unpaid' };
  }
}

interface BreakTimePolicySectionProps {
  breakBillingPolicy: BreakBillingPolicy | null | undefined;
  onBreakBillingPolicyChange: (policy: BreakBillingPolicy) => void;
  noCard?: boolean;
}

export const BreakTimePolicySection: React.FC<BreakTimePolicySectionProps> = ({
  breakBillingPolicy,
  onBreakBillingPolicyChange,
  noCard,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const mode = useMemo(() => policyToMode(breakBillingPolicy), [breakBillingPolicy]);
  const thresholdMinutes = breakBillingPolicy?.thresholdMinutes ?? 30;

  const handleModeChange = (newMode: string) => {
    onBreakBillingPolicyChange(modeToPolicy(newMode as BreakMode, thresholdMinutes));
  };

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') return;
    const parsed = parseFloat(e.target.value);
    if (!isNaN(parsed) && parsed > 0) {
      onBreakBillingPolicyChange(modeToPolicy('threshold', parsed));
    }
  };

  const summaryText = useMemo(() => {
    switch (mode) {
      case 'all_paid': return 'All Paid';
      case 'all_unpaid': return 'All Unpaid';
      case 'threshold': return `≤${thresholdMinutes}min Paid, >${thresholdMinutes}min Unpaid`;
    }
  }, [mode, thresholdMinutes]);

  const content = (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between min-h-[44px] py-3 px-2"
      >
        <div className="flex items-center gap-2">
          <Coffee className="h-4 w-4 text-muted-foreground" />
          <h2 className="heading-label text-sm text-foreground">
            Breaks
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {!isOpen && (
            <span className="text-xs text-muted-foreground">
              {summaryText}
            </span>
          )}
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180"
          )} />
        </div>
      </button>
      <AnimatedCollapse open={isOpen}>
        <div className="max-w-3xl pt-2 pb-1 px-1 space-y-3">
          <div className="grid grid-cols-1 gap-y-2 md:grid-cols-2 md:gap-4 md:items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <span className="font-label text-[12px] sm:text-[13px] uppercase tracking-wider text-muted-foreground">Break Billing Policy</span>
                <HelpButton
                  fieldName="Break Billing Policy"
                  content={mealBreakTypeMd}
                  variables={{ mealBreakLengthMinutes: thresholdMinutes || 30, String }}
                  enableFieldFocus
                />
              </div>
              <p className="text-[12px] sm:text-[13px] text-muted-foreground">
                Paid breaks count toward worked hours. Unpaid breaks are subtracted, which may push overtime to kick in sooner.
              </p>
            </div>
            <RadioGroup
              value={mode}
              onValueChange={handleModeChange}
              className="grid gap-1.5"
            >
              {([
                { value: 'all_paid', label: 'All Paid', hint: '(on the clock)' },
                { value: 'all_unpaid', label: 'All Unpaid', hint: '(off the clock)' },
                { value: 'threshold', label: 'Threshold', hint: undefined },
              ] as const).map(({ value, label, hint }) => (
                <label
                  key={value}
                  htmlFor={`break-policy-${value}`}
                  className={cn(
                    "flex items-center gap-3 rounded py-2 px-3 cursor-pointer border transition-colors duration-150",
                    mode === value
                      ? "border-l-2 border-y border-r border-border border-l-primary bg-surface-2"
                      : "border border-border hover:bg-surface-1/50"
                  )}
                >
                  <RadioGroupItem value={value} id={`break-policy-${value}`} />
                  <span className="text-sm">
                    {label}
                    {hint && <span className="text-muted-foreground text-xs ml-1">{hint}</span>}
                  </span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <AnimatedCollapse open={mode === 'threshold'}>
            <div className="grid grid-cols-1 gap-y-2 md:grid-cols-2 md:gap-4 md:items-center">
              <div className="space-y-0.5">
                <span className="font-label text-[12px] sm:text-[13px] uppercase tracking-wider text-muted-foreground">Threshold</span>
                <p className="text-[12px] sm:text-[13px] text-muted-foreground">
                  Breaks up to {thresholdMinutes} minutes: <span className="text-foreground font-medium">Paid</span>.
                  Longer than {thresholdMinutes} minutes: <span className="text-foreground font-medium">Unpaid</span>.
                </p>
              </div>
              <div className="flex justify-start md:justify-end">
                <NumberInput
                  id="break-threshold-minutes"
                  aria-label="Break threshold in minutes"
                  value={thresholdMinutes}
                  onChange={handleThresholdChange}
                  unit="min"
                  size="xs"
                  minValue={1}
                  maxValue={480}
                />
              </div>
            </div>
          </AnimatedCollapse>
        </div>
      </AnimatedCollapse>
    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <div className="equipment-panel rounded p-3 sm:p-4 max-w-3xl mx-auto">
      {content}
    </div>
  );
};
